// src/component/Livestock/ProductionChart.jsx

import React, { useRef, useEffect } from "react";
import * as echarts from "echarts";

const PALETTE = [
  "#16A34A",
  "#4ADE80",
  "#F59E0B",
  "#3B82F6",
  "#EF4444",
  "#8B5CF6",
  "#06B6D4",
];

export default function ProductionSystemChart({ data }) {
  const divRef  = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!divRef.current) return;

    if (!chartRef.current) {
      chartRef.current = echarts.init(divRef.current, null, { renderer: "svg" });
    }

    const seriesData = Object.entries(data).map(([name, { pct }], i) => ({
      name,
      value:     pct,
      itemStyle: { color: PALETTE[i % PALETTE.length] },
    }));

    const option = {
      tooltip: {
        trigger:   "item",
        formatter: (p) => `${p.name}: ${p.value}%`,
      },

      // ── Legend stays at the TOP, fixed height so it never overlaps ──────
      legend: {
        top:        0,          // pinned to very top of the chart area
        left:       "center",
        itemWidth:  8,
        itemHeight: 8,
        itemGap:    6,
        textStyle: {
          fontSize: 9,
          color:    "#6B7280",
        },
        formatter: (name) => name.replace(" System", ""),
      },

      series: [
        {
          name:              "Production System",
          type:              "pie",

          // ── Key fix: center pushed down so it clears the two legend rows ──
          // "50%" horizontal keeps it centred left-right.
          // "68%" vertical means the circle centre sits at 68% of the total
          // chart height — well below the ~30px legend area.
          center:  ["50%", "60%"],

          // Slightly smaller ring so it doesn't clip the bottom
          radius:  ["35%", "70%"],

          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 10,
            borderColor:  "#fff",
            borderWidth:  2,
          },
          label: {
            show:     false,
            position: "center",
          },
          emphasis: {
            label: {
              show:       true,
              fontSize:   12,
              fontWeight: "bold",
              color:      "#111827",
              formatter:  (p) =>
                `{n|${p.name.replace(" System", "")}}\n{v|${p.value}%}`,
              rich: {
                n: { fontSize: 8,  color: "#6B7280", lineHeight: 13 },
                v: { fontSize: 13, color: "#111827", fontWeight: 700, lineHeight: 18 },
              },
            },
          },
          labelLine: { show: false },
          data:      seriesData,
        },
      ],
    };

    chartRef.current.setOption(option);

    const ro = new ResizeObserver(() => chartRef.current?.resize());
    ro.observe(divRef.current);
    return () => ro.disconnect();
  }, [data]);

  useEffect(() => {
    return () => {
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []);

  // Taller div so there is enough room for legend + doughnut without crowding
  return <div ref={divRef} style={{ width: "100%", height: 170 }} />;
}